import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { EventoAgenda } from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { CalendarioMensual } from "../../components/CalendarioMensual";
import { ConfirmacionDialog } from "../../components/ConfirmacionDialog";
import { EncabezadoPagina } from "../../components/EncabezadoPagina";
import { Select } from "../../components/Formulario";
import { Insignia } from "../../components/Insignia";
import { Modal } from "../../components/Modal";
import { periodoInicial, SelectorPeriodo, type PeriodoSeleccionado } from "../../components/SelectorPeriodo";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { useToast } from "../../components/Toast";
import { useEstadoEnUrl } from "../../hooks/useEstadoEnUrl";
import { api, ErrorApi } from "../../lib/apiClient";
import { calcularRangoPeriodo } from "../../lib/periodo";

type EventoConExpediente = EventoAgenda & { numero_expediente: string };

const ESTADOS = ["", "Pendiente", "Vencido", "Cumplido", "Cancelado"];

// Mismo criterio que la pestaña de Agenda de un expediente (ver
// PestanaAgenda.tsx): toda transición de estado queda confirmada y
// reversible vía PATCH /agenda/:id/estado.
const CONFIRMACION_POR_ACCION: Record<
  "Cumplido" | "Cancelado" | "Pendiente",
  { titulo: string; mensaje: (titulo: string) => string; textoConfirmar: string; peligroso?: boolean }
> = {
  Cumplido: {
    titulo: "Cumplir vencimiento",
    mensaje: (t) => `¿Marcar "${t}" como cumplido?`,
    textoConfirmar: "Cumplir",
  },
  Cancelado: {
    titulo: "Cancelar vencimiento",
    mensaje: (t) => `¿Cancelar "${t}"? Vas a poder reabrirlo después si fue un error.`,
    textoConfirmar: "Cancelar vencimiento",
    peligroso: true,
  },
  Pendiente: {
    titulo: "Reabrir vencimiento",
    mensaje: (t) => `¿Volver a marcar "${t}" como pendiente?`,
    textoConfirmar: "Reabrir",
  },
};

export function AgendaPage() {
  const { mostrar } = useToast();
  const [eventos, setEventos] = useState<EventoConExpediente[]>([]);
  const [cargando, setCargando] = useState(true);
  const [estado, setEstado] = useState("");
  const [responsableId, setResponsableId] = useState("");
  const [tipoEvento, setTipoEvento] = useState("");
  const [tiposConocidos, setTiposConocidos] = useState<string[]>([]);
  const [responsablesConocidos, setResponsablesConocidos] = useState<{ id: string; nombre: string }[]>([]);
  const [vista, setVista] = useEstadoEnUrl("vista", "tabla");
  const [diaSeleccionado, setDiaSeleccionado] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<{
    evento: EventoConExpediente;
    nuevoEstado: "Cumplido" | "Cancelado" | "Pendiente";
  } | null>(null);
  const [confirmandoCargando, setConfirmandoCargando] = useState(false);

  // Sin período activo por defecto: Agenda muestra todo lo pendiente, no
  // sólo una ventana de tiempo — el período es un filtro opcional para
  // acotar, no una condición obligatoria como en Mi Trabajo.
  const [periodoActivo, setPeriodoActivo] = useState(false);
  const [periodo, setPeriodo] = useState<PeriodoSeleccionado>(periodoInicial());

  useEffect(() => {
    // Catálogo real de tipos de evento y de responsables ya usados en el
    // estudio (no existe una tabla tipos_evento, y GET /usuarios exige
    // usuarios.ver — permiso que ABOGADO/PROCURADOR/ADMINISTRATIVO no
    // tienen por defecto) para ofrecer los filtros sin depender de un
    // endpoint privilegiado ni inventar una taxonomía nueva.
    api
      .get<{ items: EventoConExpediente[] }>("/agenda")
      .then((d) => {
        setTiposConocidos(Array.from(new Set(d.items.map((e) => e.tipo_evento))).sort());
        const responsables = new Map<string, string>();
        for (const e of d.items) {
          if (e.responsable_id && e.responsable_nombre) {
            responsables.set(e.responsable_id, e.responsable_nombre);
          }
        }
        setResponsablesConocidos(
          Array.from(responsables, ([id, nombre]) => ({ id, nombre })).sort((a, b) =>
            a.nombre.localeCompare(b.nombre),
          ),
        );
      })
      .catch(() => undefined);
  }, []);

  function cargar() {
    setCargando(true);
    const p = new URLSearchParams();
    if (estado) p.set("estado", estado);
    if (responsableId) p.set("responsable_id", responsableId);
    if (tipoEvento) p.set("tipo_evento", tipoEvento);
    if (periodoActivo) {
      p.set("desde", periodo.desde);
      p.set("hasta", periodo.hasta);
    }
    api
      .get<{ items: EventoConExpediente[] }>(`/agenda?${p.toString()}`)
      .then((d) => setEventos(d.items))
      .catch(() => mostrar("No se pudo cargar la agenda.", "error"))
      .finally(() => setCargando(false));
  }

  useEffect(cargar, [estado, responsableId, tipoEvento, periodoActivo, periodo]); // eslint-disable-line react-hooks/exhaustive-deps

  async function confirmarCambioEstado() {
    if (!confirmando) return;
    setConfirmandoCargando(true);
    try {
      await api.patch(`/agenda/${confirmando.evento.id}/estado`, { estado: confirmando.nuevoEstado });
      mostrar(`Evento marcado como ${confirmando.nuevoEstado.toLowerCase()}.`, "exito");
      setConfirmando(null);
      cargar();
    } catch (err) {
      mostrar(
        err instanceof ErrorApi
          ? err.message
          : "No se pudo actualizar el evento.",
        "error",
      );
    } finally {
      setConfirmandoCargando(false);
    }
  }

  const eventosCalendario = useMemo(
    () => eventos.map((e) => ({ fecha: e.fecha_vencimiento, tipo: e.tipo_evento })),
    [eventos],
  );
  const eventosDelDia = diaSeleccionado ? eventos.filter((e) => e.fecha_vencimiento === diaSeleccionado) : [];

  const columnas: ColumnaTabla<EventoConExpediente>[] = [
    { encabezado: "Vence", render: (e) => e.fecha_vencimiento },
    { encabezado: "Título", render: (e) => e.titulo },
    { encabezado: "Tipo", render: (e) => e.tipo_evento },
    {
      encabezado: "Responsable",
      render: (e) => e.responsable_nombre ?? "—",
    },
    {
      encabezado: "Prioridad",
      render: (e) => <Insignia texto={e.prioridad} tipo="prioridad" />,
    },
    { encabezado: "Estado", render: (e) => <Insignia texto={e.estado} /> },
    {
      encabezado: "Expediente",
      render: (e) => (
        <Link
          to={`/expedientes/${e.expediente_id}?tab=agenda`}
          className="font-medium text-texto transition-colors hover:text-secundario hover:underline"
        >
          {e.numero_expediente}
        </Link>
      ),
    },
    {
      encabezado: "Acciones",
      render: (e) => (
        <div className="flex flex-wrap gap-2">
          {(e.estado === "Pendiente" || e.estado === "Vencido") && (
            <>
              <Boton
                tamano="sm"
                variante="secundario"
                onClick={() => setConfirmando({ evento: e, nuevoEstado: "Cumplido" })}
              >
                Cumplir
              </Boton>
              <Boton
                tamano="sm"
                variante="fantasma"
                onClick={() => setConfirmando({ evento: e, nuevoEstado: "Cancelado" })}
              >
                Cancelar
              </Boton>
            </>
          )}
          {(e.estado === "Cumplido" || e.estado === "Cancelado") && (
            <Boton
              tamano="sm"
              variante="secundario"
              onClick={() => setConfirmando({ evento: e, nuevoEstado: "Pendiente" })}
            >
              Reabrir
            </Boton>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <EncabezadoPagina
        titulo="Agenda"
        descripcion="Vencimientos y recordatorios de todos los expedientes. Se agendan desde la ficha de cada expediente."
      />

      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-texto">Período</span>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={periodoActivo}
                onChange={(e) => setPeriodoActivo(e.target.checked)}
                className="h-4 w-4 rounded border-borde"
                aria-label="Activar filtro de período"
              />
              {periodoActivo ? (
                <SelectorPeriodo valor={periodo} onCambiar={setPeriodo} />
              ) : (
                <span className="flex h-[38px] items-center text-sm text-texto-secundario">Todos los períodos</span>
              )}
            </div>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-texto">Responsable</span>
            <Select value={responsableId} onChange={(e) => setResponsableId(e.target.value)} className="w-44">
              <option value="">Todos</option>
              {responsablesConocidos.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nombre}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-texto">Estado</span>
            <Select value={estado} onChange={(e) => setEstado(e.target.value)} className="w-40">
              {ESTADOS.map((e) => (
                <option key={e} value={e}>
                  {e || "Todos los estados"}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-texto">Tipo</span>
            <Select value={tipoEvento} onChange={(e) => setTipoEvento(e.target.value)} className="w-48">
              <option value="">Todos los tipos</option>
              {tiposConocidos.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </label>
        </div>
        <div className="flex overflow-hidden rounded-lg border border-borde">
          <button
            type="button"
            onClick={() => setVista("tabla")}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              vista === "tabla" ? "bg-principal text-superficie" : "bg-superficie text-texto-secundario hover:text-texto"
            }`}
          >
            Tabla
          </button>
          <button
            type="button"
            onClick={() => setVista("calendario")}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              vista === "calendario" ? "bg-principal text-superficie" : "bg-superficie text-texto-secundario hover:text-texto"
            }`}
          >
            Calendario
          </button>
        </div>
      </div>

      {vista === "tabla" ? (
        <Tabla
          columnas={columnas}
          filas={eventos}
          claveFila={(e) => e.id}
          cargando={cargando}
          tituloVacio="Sin eventos en agenda"
          descripcionVacio="Agendá vencimientos desde la pestaña 'Agenda' de un expediente."
        />
      ) : (
        // Reutiliza el mismo componente que Mi Trabajo (src/components/
        // CalendarioMensual.tsx) — acá muestra los eventos que el usuario
        // tiene permiso para ver (todo el estudio, no sólo los propios).
        //
        // Sin período activo, el rango NO puede ser el mínimo/máximo de
        // eventos[]: en un estudio real ese rango cubre años, y
        // CalendarioMensual recorta defensivamente a MAX_MESES=3 arrancando
        // desde el extremo más antiguo — el mes actual (y por lo tanto
        // "hoy") quedaba fuera de la grilla sin ningún aviso. Por defecto
        // se ancla al mes actual + el siguiente, igual que el resto del
        // sistema resuelve "hoy" (America/Argentina/Mendoza).
        <CalendarioMensual
          desde={periodoActivo ? periodo.desde : calcularRangoPeriodo("mes").desde}
          hasta={periodoActivo ? periodo.hasta : calcularRangoPeriodo("mes_proximo").hasta}
          eventos={eventosCalendario}
          diaSeleccionado={diaSeleccionado}
          onSeleccionarDia={setDiaSeleccionado}
        />
      )}

      <Modal
        titulo={diaSeleccionado ? `Vencimientos del ${diaSeleccionado}` : "Detalle"}
        abierto={Boolean(diaSeleccionado)}
        onCerrar={() => setDiaSeleccionado(null)}
      >
        {eventosDelDia.length === 0 ? (
          <p className="text-sm text-texto-secundario">Sin eventos este día.</p>
        ) : (
          <ul className="divide-y divide-borde">
            {eventosDelDia.map((e) => (
              <li key={e.id} className="py-3 text-sm">
                <p className="font-medium text-texto">{e.titulo}</p>
                <p className="text-xs text-texto-secundario">{e.tipo_evento}</p>
                <Link
                  to={`/expedientes/${e.expediente_id}?tab=agenda`}
                  className="text-xs font-medium text-secundario hover:underline"
                >
                  {e.numero_expediente}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      {confirmando && (
        <ConfirmacionDialog
          abierto={Boolean(confirmando)}
          titulo={CONFIRMACION_POR_ACCION[confirmando.nuevoEstado].titulo}
          mensaje={CONFIRMACION_POR_ACCION[confirmando.nuevoEstado].mensaje(confirmando.evento.titulo)}
          textoConfirmar={CONFIRMACION_POR_ACCION[confirmando.nuevoEstado].textoConfirmar}
          peligroso={CONFIRMACION_POR_ACCION[confirmando.nuevoEstado].peligroso}
          cargando={confirmandoCargando}
          onConfirmar={confirmarCambioEstado}
          onCancelar={() => setConfirmando(null)}
        />
      )}
    </div>
  );
}
